import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import uniqBy from 'lodash/uniqBy';
import React, { useState } from 'react';
import styled from 'styled-components';

import CaseStudy from '../models/CaseStudy/CaseStudy';
import RelatedCaseStudy from '../models/CaseStudy/RelatedCaseStudy';
import CaseStudyPropTypes from '../models/CaseStudy/CaseStudyPropTypes';

import JsonLd from '../components/jsonld';
import BackgroundHero from '../components/background-hero';
import ContactForm from '../components/contact-form';
import LegacyFeaturedCaseStudiesSection from '../components/legacy-featured-case-studies-section';
import Layout from '../components/layout';
import Metric from '../components/metric';
import SEO from '../components/seo';
import Video from '../components/video';
import CanonicalLink from '../components/canonical-link';

import {
  Avatar,
  Container,
  Grid,
  GridItem,
  Heading,
  InternalLink,
  Label,
  Paper,
  Text,
  animation,
  colors,
  fontSizes,
  media
} from '../ui-components';

import CONTENT from '../static-content/case-studies.json';

import Quote from '../assets/icons/quote.svg';

import renderRichText from '../utils/render-rich-text';
import { extractGlobalNodes } from '../utils/cross-space-reference-helper.jsx';
import { dedupeGeoSpecificIndustries } from '../utils/contentful-dedupe.jsx';

const propTypes = {
  data: CaseStudyPropTypes.isRequired
};

/**
 * Template for a case study page
 *
 * @component CaseStudyTemplate
 *
 * @param {object} props
 *
 * @prop {object} data
 * @prop {object} pageContext
 *
 *
 * @returns {JSX} returns case study
 */
const CaseStudyTemplate = props => {
  const [seoSlug, setSeoSlug] = useState('');
  const { data, pageContext } = props;
  const caseStudy = new CaseStudy(data.contentfulCaseStudy);

  const spaceId = pageContext.spaceId;

  let matchedIndustries = caseStudy.relatedIndustries;
  let matchedServiceLines = caseStudy.relatedServiceLines;
  let matchedTools = caseStudy.tools;
  let relatedGlobalCaseStudies = [];
  const isRegionalContent = pageContext.spaceId !== process.env.CONTENTFUL_GLOBAL_SPACE_ID;
  const handleSeoSlugChange = value => {
    setSeoSlug(value);
  };
  // If this is a global case study, we don't need to look by global nodes

  if (isRegionalContent) {
    const industries = pageContext.industries;
    const serviceLines = pageContext.serviceLines;
    const caseStudies = pageContext.caseStudies;

    const globalIndustries = props.data.allContentfulIndustry;
    const globalServiceLines = props.data.allContentfulServiceLine;
    const globalCaseStudies = props.data.allContentfulCaseStudy;

    matchedIndustries = extractGlobalNodes(industries, globalIndustries);
    matchedServiceLines = extractGlobalNodes(serviceLines, globalServiceLines);

    let matchedCaseStudies = extractGlobalNodes(caseStudies, globalCaseStudies);
    relatedGlobalCaseStudies = matchedCaseStudies.map(caseStudy => new RelatedCaseStudy(caseStudy));
  }

  matchedIndustries = dedupeGeoSpecificIndustries(matchedIndustries, pageContext.space);

  const {
    theChallengeTitle,
    theChallengeContentRichJSON,
    theChallengeMediaData,
    theSolutionTitle,
    theSolutionContentRichJSON,
    theSolutionMediaData,
    theResultsTitle,
    theResultsContentRichJSON,
    theResultsMediaData,
    relatedCaseStudies: relatedLocalCaseStudies
  } = caseStudy;

  const relatedCaseStudies = (relatedLocalCaseStudies ? relatedLocalCaseStudies : []).concat(relatedGlobalCaseStudies);

  /**
   * Renders the SEO metatag and structured data elements
   *
   * @function renderSEO
   *
   * @returns {JSX}
   */
  const renderSEO = () => {
    const { metaTitle, metaDescription, snippet, title, slug, heroImageData, categories } = caseStudy;

    return (
      <>
        <SEO
          title={metaTitle}
          description={metaDescription}
          slug={seoSlug}
          image={`${heroImageData?.fluid?.images?.fallback?.src || null}`}
        />
        <CanonicalLink
          isRegionalContent={isRegionalContent}
          contentType={'case-studies'}
          contentSlug={caseStudy.slug}
          onSeoSlugChange={handleSeoSlugChange}
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            name: metaTitle,
            description: metaDescription,
            abstract: snippet,
            headline: title,
            identifier: slug,
            inLanguage: 'en',
            author: 'Credera',
            url: `http://credera.com/case-studies/${slug}`,
            keywords: categories.join(', '),
            isAccessibleForFree: true,
            datePublished: caseStudy.getFormattedModificationDate('YYYY-MM-DD'),
            dateModified: caseStudy.getFormattedModificationDate('YYYY-MM-DD'),
            dateCreated: caseStudy.getFormattedCreationDate('YYYY-MM-DD'),
            publisher: 'https://www.credera.com',
            image: `https:${heroImageData?.fluid?.images?.fallback?.src}`
          }}
        />
      </>
    );
  };

  /**
   * Renders the Hero section
   *
   * @function renderHero
   *
   * @returns {JSX}
   */
  const renderHero = () => {
    const { heroImageData, title, clientName, snippet } = caseStudy;

    return (
      <Background>
        <CaseStudyBackgroundHero fluid={heroImageData.fluid} alt={title}></CaseStudyBackgroundHero>
        <Container>
          <TitleWrapper>
            <Label>{clientName}</Label>
            <TitleHeading level={2}>{title}</TitleHeading>
            <SnippetText size="large">{snippet}</SnippetText>
          </TitleWrapper>
        </Container>
      </Background>
    );
  };

  /**
   * Conditionally renders video if video is provided
   *
   * @function renderVideo
   *
   * @returns {JSX}
   */
  const renderVideo = () => {
    const { video } = caseStudy;

    return (
      <Container condensed>
        <CaseStudyVideo title={video.title} embedUrl={video.embedUrl} />
      </Container>
    );
  };

  /**
   * Renders the At A Glace section
   *
   * @function renderAtAGlanceSection
   *
   * @returns {JSX}
   */
  const renderAtAGlanceSection = () => {
    const { atAGlanceRichJSON } = caseStudy;

    const relatedIndustries = matchedIndustries;
    const relatedServiceLines = matchedServiceLines;
    const relatedTools = matchedTools;

    //small helper function for rendering arrow on industry & offering sections
    const overflowTitle = title => {
      return title.length > 29;
    };

    const renderIndustryList = () => {
      const uniqueIndustries = uniqBy(relatedIndustries, 'slug');
      return (
        <>
          <Label size="small">{CONTENT.INDUSTRY__TEXT}</Label>
          {uniqueIndustries.map(
            industry =>
              industry &&
              industry.slug && (
                <ListInternalLink
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  adjustedArrow={overflowTitle(industry.title)}
                  withArrow
                  hoverUnderline
                >
                  {industry.title}
                </ListInternalLink>
              )
          )}
        </>
      );
    };

    const renderOfferingsList = () => {
      return (
        <>
          <ListLabel size="small">{CONTENT.OFFERINGS__TEXT}</ListLabel>
          {relatedServiceLines.map(
            serviceLine =>
              serviceLine &&
              serviceLine.slug &&
              serviceLine.service?.[0]?.slug && (
                <ListInternalLink
                  key={serviceLine.slug}
                  to={`/services/${serviceLine.service?.[0].slug}/${serviceLine.slug}`}
                  adjustedArrow={overflowTitle(serviceLine.title)}
                  withArrow
                  hoverUnderline
                >
                  {serviceLine.title}
                </ListInternalLink>
              )
          )}
        </>
      );
    };

    const renderToolsList = () => {
      return (
        <>
          <ListLabel size="small">{CONTENT.TOOLS__TEXT}</ListLabel>
          {relatedTools.map(
            tool =>
              tool && (
                <ListInternalLink
                  key={tool}
                  to={`/case-studies?displayCount=6&tool=${tool.replace(/\s/g, '+')}`}
                  adjustedArrow={overflowTitle(tool)}
                  withArrow
                  hoverUnderline
                >
                  {tool}
                </ListInternalLink>
              )
          )}
        </>
      );
    };

    return (
      <Paper bottom>
        <Container condensed>
          <Grid>
            <GridItem col={8} colmd={12}>
              <div>
                <Label>{CONTENT.AT_A_GLANCE__TEXT}</Label>
                {atAGlanceRichJSON && renderRichText(atAGlanceRichJSON, pageContext.space)}
              </div>
            </GridItem>
            <GridItem col={4} colmd={12}>
              <div>
                {relatedIndustries && relatedIndustries.length > 0 && renderIndustryList()}
                {relatedServiceLines && relatedServiceLines.length > 0 && renderOfferingsList()}
                {relatedTools && relatedTools.length > 0 && renderToolsList()}
              </div>
            </GridItem>
          </Grid>
        </Container>
        {caseStudy.video && caseStudy.videoBlockAtTop && renderVideo()}
      </Paper>
    );
  };
  /**
   * Renders the content section based on the passed values
   *
   * @function renderSection
   *
   * @param {string} label
   * @param {string} title
   * @param {object} richText
   * @param {object} image
   * @param {boolean} hasBackground
   *
   * @returns {JSX}
   */
  const renderSection = (label, title, richText, image, hasBackground) => {
    return (
      <>
        <SectionContainer condensed>
          <Label>{label}</Label>
          <SectionHeading level={4}>{title}</SectionHeading>
          {richText && renderRichText(richText, pageContext.space)}
        </SectionContainer>
        {image.fluid && (
          <MediaContainer>
            {hasBackground ? (
              <BackgroundWrapper>
                <Image image={image?.fluid} alt={image?.title} />
              </BackgroundWrapper>
            ) : (
              <Image image={image?.fluid} alt={image?.title} />
            )}
          </MediaContainer>
        )}
      </>
    );
  };

  /**
   * Renders the metrics section
   *
   * @function renderMetrics
   *
   * @returns {JSX}
   */
  const renderMetrics = () => {
    const { metricsBlock } = caseStudy;

    return (
      <SectionContainer condensed>
        <Grid>
          {metricsBlock.map((metric, index) => (
            <MetricGridItem
              key={index}
              col={metricsBlock.length > 2 ? 4 : 6}
              hasBorder={index === metricsBlock.length - 1 ? false : true}
            >
              <Metric title={metric.title} percentage={metric.percentage} isCentered />
            </MetricGridItem>
          ))}
        </Grid>
      </SectionContainer>
    );
  };

  /**
   * Renders the quote block
   *
   * @function renderQuoteBlock
   *
   * @returns {JSX}
   */
  const renderQuoteBlock = () => {
    const { quoteBlock } = caseStudy;
    const { quote, avatarData, author, authorSubtitle } = quoteBlock;

    return (
      <SectionContainer condensed>
        <img src={Quote} alt={author} />
        <QuoteHeading level={4}>{quote}</QuoteHeading>
        {avatarData && <Avatar name={author} title={authorSubtitle} image={avatarData} />}
      </SectionContainer>
    );
  };

  /**
   * Renders the Related Case Studies section
   *
   * @function renderCaseStudiesSection
   *
   * @returns {JSX}
   */
  const renderCaseStudiesSection = () => {
    return (
      <Paper top>
        <Container sectionContainer>
          <SubTitleHeading level={2}>{CONTENT.RELATED_CASE_STUDIES__TEXT}</SubTitleHeading>
          <LegacyFeaturedCaseStudiesSection caseStudies={relatedCaseStudies} />
        </Container>
      </Paper>
    );
  };

  return (
    <Layout>
      {renderSEO()}
      {renderHero()}
      {renderAtAGlanceSection()}
      {theChallengeTitle &&
        renderSection(
          CONTENT.THE_CHALLENGE__TEXT,
          theChallengeTitle,
          theChallengeContentRichJSON,
          theChallengeMediaData,
          false
        )}
      {theSolutionTitle &&
        renderSection(
          CONTENT.THE_SOLUTION__TEXT,
          theSolutionTitle,
          theSolutionContentRichJSON,
          theSolutionMediaData,
          true
        )}
      {theResultsTitle &&
        renderSection(
          CONTENT.THE_RESULTS__TEXT,
          theResultsTitle,
          theResultsContentRichJSON,
          theResultsMediaData,
          false
        )}
      {caseStudy.quoteBlock && renderQuoteBlock()}
      {caseStudy.video && !caseStudy.videoBlockAtTop && renderVideo()}
      {caseStudy.metricsBlock && renderMetrics()}
      {relatedCaseStudies.length > 0 && renderCaseStudiesSection()}
      <ContactForm geoSpace={pageContext.space} slug={pageContext.slug} />
    </Layout>
  );
};

CaseStudyTemplate.propTypes = propTypes;

export default CaseStudyTemplate;

const Background = styled.div`
  background-color: ${colors.sandBeige};
`;

const CaseStudyBackgroundHero = styled(props => <BackgroundHero {...props} />)`
  height: 580px;
  margin-bottom: -100px;
  position: relative;
  z-index: 1;

  @media screen and (max-width: ${media.largeBottom}) {
    height: 360px;
  }

  @media screen and (max-width: ${media.smallTop}) {
    height: 240px;
  }
`;

const TitleWrapper = styled.div`
  background-color: ${colors.pureWhite};
  text-align: center;
  padding: 30px 40px;
  max-width: 980px;
  margin: auto;
  position: relative;
  z-index: 2;
`;

const TitleHeading = styled(props => <Heading {...props} />)`
  margin-bottom: 12px;

  @media (max-width: ${media.smallTop}) {
    font-size: ${fontSizes.h5.fontSize};
    line-height: ${fontSizes.h5.lineHeight};
  }
`;

const SnippetText = styled(props => <Text {...props} />)`
  margin-bottom: 12px;
`;

const SubTitleHeading = styled(Heading)`
  margin-bottom: 20px;
`;

const ListLabel = styled(Label)`
  margin-top: 40px;
`;

const ListInternalLink = styled(InternalLink)`
  color: ${colors.midnightBlack};
  text-transform: capitalize;
  font-weight: 400;
  margin-bottom: 16px;
  width: fit-content;

  svg {
    color: ${colors.oceanBlue};
    overflow: visible;
    /* we need these margin-right css selectors for when text wraps in offering/industry section because the svg arrow is disrupted. px values were selected through seeing where dimensions cause the page to break.  */
    margin-right: ${({ adjustedArrow }) => (adjustedArrow ? `73px` : `0px`)};

    @media screen and (max-width: 550px) {
      margin-right: ${({ adjustedArrow }) => (adjustedArrow ? `211px` : `0px`)};
    }

    @media screen and (max-width: 430px) {
      margin-right: ${({ adjustedArrow }) => (adjustedArrow ? `109px` : `0px`)};
    }

    @media screen and (max-width: 325px) {
      margin-right: 0px;
    }
  }

  &:hover {
    svg {
      color: ${colors.linkHover};
      transition: color ${animation.general.durationAndTiming};
    }
  }
`;

const CaseStudyVideo = styled(Video)`
  margin-top: 0;
`;

const Image = styled(props => <GatsbyImage {...props} />)`
  width: 100%;
`;

const SectionContainer = styled(Container)`
  padding-top: 20px;
  padding-bottom: 20px;

  @media (min-width: ${media.mediumTop}) {
    padding-top: 60px;
    padding-bottom: 60px;
  }

  @media (min-width: ${media.largeTop}) {
  }
`;

const MediaContainer = styled(Container)`
  padding: 20px 0;

  @media (min-width: ${media.mediumBottom}) {
    padding: 36px 24px;
  }

  @media (min-width: ${media.largeBottom}) {
    padding: 36px;
  }
`;

const BackgroundWrapper = styled.div`
  padding: 25px 20px;
  background-color: ${colors.lightSky};

  @media (min-width: ${media.mediumBottom}) {
    padding: 50px;
  }

  @media (min-width: ${media.largeBottom}) {
    padding: 100px;
  }
`;

const SectionHeading = styled(Heading)`
  margin-top: 20px;
  margin-bottom: 20px;
  line-height: 36px;

  @media (min-width: ${media.mediumBottom}) {
    line-height: 44px;
  }
`;

const QuoteHeading = styled(Heading)`
  font-size: 24px;
  line-height: 36px;

  @media (min-width: ${media.mediumBottom}) {
    font-size: 32px;
    line-height: 44px;
  }
`;

const MetricGridItem = styled(GridItem)`
  border-bottom: ${({ hasBorder }) => (hasBorder ? `1px solid ${colors.gray3}` : 'none')};
  padding: 20px 0;

  @media (min-width: ${media.mediumBottom}) {
    padding: 0;
    border-bottom: 0;
    border-right: ${({ hasBorder }) => (hasBorder ? `1px solid ${colors.gray3}` : 'none')};
  }
`;

export const pageQuery = graphql`
  query CaseStudyBySlug($slug: String!, $spaceId: String!, $globalSpaceId: String!) {
    site {
      siteMetadata {
        title
      }
    }
    contentfulCaseStudy(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompleteCaseStudy
    }
    allContentfulIndustry(filter: { spaceId: { eq: $globalSpaceId } }) {
      nodes {
        contentful_id
        title
        slug
      }
    }
    allContentfulServiceLine(filter: { spaceId: { eq: $globalSpaceId } }) {
      nodes {
        contentful_id
        title
        slug
        service {
          slug
        }
      }
    }
    allContentfulCaseStudy(filter: { spaceId: { eq: $globalSpaceId } }) {
      nodes {
        contentful_id
        slug
        title
        snippet {
          snippet
        }
        heroImage {
          gatsbyImageData(layout: CONSTRAINED, width: 1280)
        }
        categories
        clientName
      }
    }
  }
`;
